import React from 'react';
import {
  Box,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { DEFAULT_TYPE_KEY, TypeLeadTime, TypeLeadTimes } from '../../services/reminderService';
import ReminderDaysEditor from './ReminderDaysEditor';

/** Friendly names for the reminder types the timing editor exposes. */
export const TYPE_LABELS: Record<string, string> = {
  [DEFAULT_TYPE_KEY]: 'Everything else',
  vaccination: 'Vaccinations',
  coggins: 'Coggins / EIA',
  dental: 'Dentals',
  lab: 'Lab work',
  exam: 'Exams',
  recheck: 'Rechecks',
  deworming: 'Deworming',
};

/** Mirror the backend caps (reminder-lead-times.ts) so nothing surprises on save. */
const MAX_TOUCHES = 5;
const MAX_DAYS = 180;

interface Props {
  value: TypeLeadTimes;
  onChange: (next: TypeLeadTimes) => void;
  disabled?: boolean;
}

/**
 * Per-type notice-timing editor (ADR-0021): each stage is a list of day rows (ReminderDaysEditor,
 * the same control as appointment reminders), plus an on/off switch per type. Every box is typed
 * into directly, so keyboard-only entry works (the v2 CSV boxes were the launch blocker for that).
 */
const TypeLeadTimeEditor: React.FC<Props> = ({ value, onChange, disabled }) => {
  const entryFor = (key: string): TypeLeadTime =>
    value[key] ?? { preDays: [], overdueDays: [] };

  const patchEntry = (key: string, patch: Partial<TypeLeadTime>) => {
    const entry = entryFor(key);
    const next: TypeLeadTime = { ...entry, ...patch };
    // The flag is stored only when false — absent means enabled (backend rule).
    if (next.enabled !== false) delete next.enabled;
    onChange({ ...value, [key]: next });
  };

  const dayRows = (key: string, field: 'preDays' | 'overdueDays') => {
    const entry = entryFor(key);
    const before = field === 'preDays';
    const word = before ? 'before' : 'after';
    return (
      <ReminderDaysEditor
        compact
        // Before due reads furthest first; after due reads soonest first.
        days={before ? entry[field] : [...entry[field]].sort((a, b) => a - b)}
        order={before ? 'desc' : 'asc'}
        onChange={(next) => patchEntry(key, { [field]: next })}
        maxCount={MAX_TOUCHES}
        maxDays={MAX_DAYS}
        unit={(d) => (d === 1 ? 'day' : 'days')}
        fieldLabel={(i) => `${TYPE_LABELS[key]}, notice ${i + 1}, days ${word} due`}
        removeLabel={(i) => `Remove ${TYPE_LABELS[key]} notice ${i + 1} ${word} due`}
        addLabel={`Add a ${TYPE_LABELS[key]} notice ${word} due`}
        disabled={disabled || entry.enabled === false}
      />
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Reminder</TableCell>
            <TableCell>Days before due</TableCell>
            <TableCell>Days after due</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(TYPE_LABELS).map((key) => {
            const entry = entryFor(key);
            const typeOff = entry.enabled === false;
            return (
              <TableRow key={key} sx={typeOff ? { opacity: 0.55 } : undefined}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip
                      title={
                        typeOff
                          ? `Turn ${TYPE_LABELS[key]} reminders back on`
                          : `Stop sending ${TYPE_LABELS[key]} reminders`
                      }
                    >
                      <Switch
                        size="small"
                        checked={!typeOff}
                        disabled={disabled}
                        onChange={(e) =>
                          patchEntry(key, { enabled: e.target.checked ? undefined : false })
                        }
                        inputProps={{ 'aria-label': `${TYPE_LABELS[key]} reminders on/off` }}
                      />
                    </Tooltip>
                    <Typography variant="body2">{TYPE_LABELS[key]}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{dayRows(key, 'preDays')}</TableCell>
                <TableCell>{dayRows(key, 'overdueDays')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Up to {MAX_TOUCHES} notices before and {MAX_TOUCHES} after, 0 to {MAX_DAYS} days each. An empty
          column sends nothing at that stage.
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default TypeLeadTimeEditor;
